<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch, nextTick, onUnmounted, computed } from "vue";
import OT from "@opentok/client";

import {
  PARTICIPANT,
  TRACK_KIND,
  VONAGE_EXCEPTION_NAMES,
  VIDEO_CALL_EXCEPTIONS,
  VIDEO_CALL_NETWORK_QUALITY,
  VIDEO_CALL_STATUS,
  VONAGE_EXCEPTION_CODES,
} from "../definitions";
import type { DevicesInfo, VideoCallConfig, VideoDimensions, VonageError, trackStatus } from "../definitions";
import type { User } from "@/Modules/Models/Domain/User";

import TcAvatar from "@/Components/Extend/TcAvatar.vue";
import TcButton from "@/Components/UI/TcButton.vue";

import useAppStore from "@/Modules/App/Store";
import useVideoCallStore from "@/Modules/VideoCall/Store";
import useUserStore from "@/Modules/User/Store";

import { hashCode, isValidString } from "@/Utils/String";
import { danger as dangerLogger, log as logger } from "@/Utils/Debug";
import { hasValue } from "@/Utils/Objects";
import { waitFor } from "@/Utils/Promises";

import { getPublisher } from "./utils/Vonage";
import { startRecorder } from "./utils/RecordAudio";

import { generateParticipantStyles } from "../utils";
import { DEFAULT_RESOLUTION, MIN_SCALE } from "../constants";

export interface CallParticipantProps {
  participant: PARTICIPANT;
  session: OT.Session;
  stream?: OT.Stream;
  token?: string;
  scale?: number;
  blur?: boolean;
  user?: Pick<User, "avatar" | "username" | "id">;
  trackStatus: trackStatus;
  canConnect?: boolean;
  transcription?: boolean;
  networkQuality: VIDEO_CALL_NETWORK_QUALITY;
  devices?: DevicesInfo;
  videoConfig?: VideoCallConfig;
}

const MAX_RETRIES = 5;
const RETRY_DELAY = 4; // IN SECONDS

const appStore = useAppStore();
const videoCallStore = useVideoCallStore();
const userStore = useUserStore();

const props = withDefaults(defineProps<CallParticipantProps>(), {
  isPublisher: false,
  scale: MIN_SCALE,
  canConnect: false,
  transcription: false,
});

const emit = defineEmits<{
  (event: "published", value: OT.Publisher): void;
  (event: "publisherReady", value: OT.Publisher): void;
  (event: "streamSubscribed"): void;
  (event: "streamDestroy"): void;
  (event: "error", value: { code: VIDEO_CALL_EXCEPTIONS; exception?: unknown }): void;
  (event: "trackStopped", value: TRACK_KIND): void;
  (event: "trackEnabled", value: TRACK_KIND): void;
  (event: "updateDimensions"): void;
  (event: "forceStopVideo", value: PARTICIPANT): void;
  (event: "forceEnableVideo", value: PARTICIPANT): void;
  (event: "changeStreamQuality", value: VIDEO_CALL_NETWORK_QUALITY): void;
  (event: "unpublish", value: PARTICIPANT): void;
  (event: "stopTrack", value: TRACK_KIND): void;
}>();

const videoWrapperRef = ref<HTMLElement>();
const isReadyPublisher = ref<boolean>(false);
const isErrorAvatarLocal = ref<boolean>(false);
const styleWrapper = ref<Record<string, string>>({});
const currentRetry = ref<number>(0);
const intervalSyncTrack = ref<NodeJS.Timeout>();

let publisher: OT.Publisher | undefined = undefined;
let subscriber: OT.Subscriber | undefined = undefined;
let audioStream: MediaStream | undefined = undefined;
let roomId: string | undefined = undefined;

const log = (...args: unknown[]) => {
  logger(`[${props.participant}]`, ...args);
};

const danger = (...args: unknown[]) => {
  dangerLogger(`[${props.participant}]`, ...args);
};

const isInvertedVideo = computed(() => {
  if (hasValue(props.videoConfig)) {
    return props.videoConfig.mirror;
  }

  return false;
});

const handleUpdateStyle = (dimensions: VideoDimensions) => {
  styleWrapper.value = generateParticipantStyles(dimensions, appStore.isMobileViewport);

  nextTick(() => emit("updateDimensions"));
};

const handleVideoReady = (video: HTMLVideoElement | HTMLObjectElement) => {
  log("Video ready");

  if (hasValue(videoWrapperRef.value) && document.body.contains(videoWrapperRef.value)) {
    videoWrapperRef.value!.appendChild(video);
  }

  if (props.participant === PARTICIPANT.LOCAL && hasValue(publisher)) {
    waitFor(0.7).then(() => {
      nextTick(() =>
        handleUpdateStyle({ width: publisher!.videoWidth()!, height: publisher!.videoHeight()! })
      );
    });
  }
};

const handleInitPublisher = (p?: OT.Publisher) => {
  if (hasValue(p)) {
    log("Init publisher", p);

    publisher = p;
    isReadyPublisher.value = true;
    intervalSyncTrack.value = setInterval(syncStreamTracks, 6000);
    publishStreams();

    const config = videoCallStore.getConfig();

    if (props.participant === PARTICIPANT.LOCAL) {
      if (config?.video === false && hasValue(config.videoId)) {
        emit("stopTrack", TRACK_KIND.VIDEO);
      }

      if (config?.audio === false) {
        emit("stopTrack", TRACK_KIND.AUDIO);
      }
    }
  } else {
    emit("error", { code: VIDEO_CALL_EXCEPTIONS.CLOSE_CALL_BEFORE_INIT });
  }
};

const handleInitError = (err?: OT.OTError) => {
  danger("Error init publisher", err);

  emit("error", { code: VIDEO_CALL_EXCEPTIONS.INIT_PUBLISHER, exception: err });

  isReadyPublisher.value = false;

  if (props.participant === PARTICIPANT.SHARE_SCREEN) {
    emit("streamDestroy");
  }

  return undefined;
};

const handleErrorPublishStreams = (err?: OT.OTError) => {
  if (hasValue(err)) {
    emit("error", { code: VIDEO_CALL_EXCEPTIONS.PUBLISH_STREAMS, exception: err });
  } else {
    log("Streams published");

    emit("published", publisher!);

    if (props.participant === PARTICIPANT.SHARE_SCREEN) {
      publisher!.on("streamDestroyed", () => {
        emit("streamDestroy");
      });
    }

    if (hasValue(publisher?.stream) && hasValue(publisher.stream.videoDimensions)) {
      handleUpdateStyle(publisher.stream.videoDimensions);
    }
  }
};

const handleErrorConnectSession = (err?: VonageError) => {
  if (hasValue(err)) {
    if (hasValue(err.code) && err.code === VONAGE_EXCEPTION_CODES.RETRY_CONNECT) {
      return handeRetryConnect();
    } else if (err.name === VONAGE_EXCEPTION_NAMES.OT_INVALID_HTTP_STATUS) {
      return handeRetryConnect();
    } else {
      emit("error", { code: VIDEO_CALL_EXCEPTIONS.CONNECT_SESSION, exception: err });
    }
  } else {
    log("Connected to session", props.session.capabilities);

    props.session.publish(publisher!, handleErrorPublishStreams);

    publisher!.on("videoDisableWarning", () => {
      emit("forceStopVideo", PARTICIPANT.LOCAL);
    });

    publisher!.on("videoDisableWarningLifted", () => {
      emit("forceEnableVideo", PARTICIPANT.LOCAL);
    });
  }
};

const handleErrorSubscription = (err?: OT.OTError) => {
  if (hasValue(err)) {
    emit("error", { code: VIDEO_CALL_EXCEPTIONS.ERROR_SUBSCRIBE_STREAM, exception: err });
  } else {
    const videoCallConfig = videoCallStore.getConfig();

    props.session.signal(
      { type: "config", data: JSON.stringify({ userId: userStore.user!.id, ...videoCallConfig }) },
      (err?) => {
        if (hasValue(err)) {
          log("Error sending config", err, videoCallConfig);
        } else {
          log("Config sent", videoCallConfig);
        }
      }
    );

    intervalSyncTrack.value = setInterval(syncStreamTracks, 5000);

    waitFor(0.3).then(() => {
      emit("streamSubscribed");
    });
  }
};

const handlePublishScreenError = (err?: OT.OTError) => {
  if (hasValue(err)) {
    emit("error", { code: VIDEO_CALL_EXCEPTIONS.ERROR_PUBLISH_SCREEN, exception: err });
  } else {
    handleErrorPublishStreams(undefined);
  }
};

const publishStreams = () => {
  if (isReadyPublisher.value && hasValue(publisher)) {
    if (props.participant === PARTICIPANT.LOCAL) {
      if (props.canConnect) {
        log("connect to session");

        handleConnectToVideoCall();
      } else {
        if (hasValue(videoWrapperRef.value)) {
          const videos = videoWrapperRef.value.getElementsByTagName("video");

          if (videos.length > 0) {
            handleUpdateStyle({ height: videos[0].videoHeight, width: videos[0].videoWidth });
          }
        }

        emit("publisherReady", publisher!);
      }
    } else if (props.participant === PARTICIPANT.SHARE_SCREEN) {
      props.session.publish(publisher!, handlePublishScreenError);
    }
  }
};

const addSubscriberHandlers = () => {
  if (hasValue(subscriber)) {
    log("ADDED SUBSCRIBER HANDLERS");

    subscriber.on("videoDisabled", (reason) => {
      log("Remote video disabled", reason);
      emit("trackStopped", TRACK_KIND.VIDEO);
    });

    subscriber.on("videoEnabled", (reason) => {
      log("Remote video enabled", reason);
      emit("trackEnabled", TRACK_KIND.VIDEO);
    });

    subscriber.on("disconnected", () => {
      log("Remote video disconnected");
      emit("changeStreamQuality", VIDEO_CALL_NETWORK_QUALITY.LOW);

      waitFor(1).then(() => {
        emit("trackStopped", TRACK_KIND.VIDEO);
      });
    });

    subscriber.on("connected", () => {
      log("Remote video connected");
      emit("changeStreamQuality", VIDEO_CALL_NETWORK_QUALITY.HIGHEST);

      waitFor(1).then(() => {
        syncStreamTracks();
      });
    });

    if (props.participant === PARTICIPANT.REMOTE) {
      props.session.on("streamPropertyChanged", (event) => {
        if (event.stream.streamId === props.stream!.streamId && event.changedProperty === "hasAudio") {
          if (props.trackStatus.audio !== event.newValue) {
            if (event.newValue) {
              log("Remote audio enabled", event);
              emit("trackEnabled", TRACK_KIND.AUDIO);
            } else {
              log("Remote audio disabled", event);
              emit("trackStopped", TRACK_KIND.AUDIO);
            }
          }
        }
      });

      props.session.on("streamCreated", (event) => {
        log("Remote stream created", event);

        if (
          hasValue(event.stream) &&
          isValidString(event.stream.streamId) &&
          event.stream.videoType !== "screen"
        ) {
          initSubcriber(event.stream);
        }
      });
    }

    subscriber.on("videoDimensionsChanged", (event) => {
      handleUpdateStyle(event.newValue);
    });
  }
};

const handleConnectToVideoCall = () => {
  if (hasValue(publisher)) {
    props.session.connect(props.token!, handleErrorConnectSession);
  }
};

const handeRetryConnect = () => {
  log("Try connect", currentRetry.value + 1);
  if (currentRetry.value < MAX_RETRIES) {
    currentRetry.value++;

    waitFor(RETRY_DELAY).then(handleConnectToVideoCall);
  } else {
    emit("error", {
      code: VIDEO_CALL_EXCEPTIONS.CONNECT_SESSION_RETRY,
      exception: new Error("Retry connect"),
    });
  }
};

const initSubcriber = (stream: OT.Stream) => {
  log("Init subcriber stream", stream.streamId);

  const videos = videoWrapperRef.value!.getElementsByTagName("video");

  for (let i = 0; i < videos.length; i++) {
    if (hasValue(videos[i])) {
      videos[i].remove();
    }
  }

  subscriber = props.session.subscribe(
    stream,
    undefined,
    {
      insertDefaultUI: false,
      insertMode: "replace",
      fitMode: "contain",
      height: "100%",
      width: "100%",
    },
    (err) => {
      if (hasValue(err)) {
        handleErrorSubscription(err);
      }
    }
  );

  if (hasValue(subscriber)) {
    subscriber.on("videoElementCreated", (event) => {
      log("VideoElementCreated", event, event.element.width, event.element.height);
      videoWrapperRef.value!.appendChild(event.element);
      handleErrorSubscription(undefined);
    });
  }

  handleUpdateStyle(stream.videoDimensions);
  nextTick(() => emit("updateDimensions"));

  if (hasValue(subscriber)) {
    addSubscriberHandlers();
  }
};

const syncStatus = (OtStatus: trackStatus) => {
  if (OtStatus.video !== props.trackStatus.video) {
    log("Sync video status", OtStatus.video);

    if (OtStatus.video) {
      emit("trackEnabled", TRACK_KIND.VIDEO);
    } else {
      emit("trackStopped", TRACK_KIND.VIDEO);
    }
  }

  if (OtStatus.audio !== props.trackStatus.audio) {
    log("Sync audio status", OtStatus.video);

    if (OtStatus.video) {
      emit("trackEnabled", TRACK_KIND.AUDIO);
    } else {
      emit("trackStopped", TRACK_KIND.AUDIO);
    }
  }
};

const syncStreamTracks = () => {
  log("Check streams status for", props.participant);

  if (hasValue(publisher?.stream)) {
    syncStatus({ video: publisher.stream.hasVideo, audio: publisher.stream.hasAudio });
  } else if (hasValue(subscriber?.stream)) {
    syncStatus({ video: subscriber.stream.hasVideo, audio: subscriber.stream.hasAudio });
  }
};

const callStatusWhatcher = watch(
  () => videoCallStore.status,
  async (newValue, oldValue) => {
    if (hasValue(newValue) && newValue !== oldValue) {
      if (newValue === VIDEO_CALL_STATUS.CALLING) {
        if (props.transcription && props.participant === PARTICIPANT.LOCAL) {
          audioStream = new MediaStream();
          audioStream.addTrack(publisher!.getAudioSource());

          startRecorder(audioStream, (blob: Blob) => {
            log("SEND AUDIO TRANSCRIPTION", blob.size);

            videoCallStore.sendAudio(blob);
          });
        }
      } else if (newValue === VIDEO_CALL_STATUS.UNPUBLISHING) {
        log("Unpublish streams");

        if (props.participant === PARTICIPANT.LOCAL) {
          if (isReadyPublisher.value && hasValue(publisher)) {
            publisher.getStats(function (err, stats) {
              if (err || !hasValue(stats)) {
                log("Error getting stats: ", err);
              } else {
                log("STATS", stats, videoCallStore.roomId!);
                videoCallStore.sendVideoCallLogs(roomId!, stats[0]);
              }

              if (hasValue(publisher)) {
                publisher.publishVideo(false);
                publisher.publishAudio(false);
                publisher.destroy();
              }

              publisher = undefined;
              roomId = undefined;

              emit("unpublish", props.participant);
            });
          } else {
            emit("unpublish", props.participant);
          }
        } else if (
          props.participant === PARTICIPANT.SHARE_SCREEN &&
          hasValue(publisher) &&
          isReadyPublisher.value
        ) {
          props.session!.unpublish(publisher);

          emit("unpublish", props.participant);
        }
      } else if (newValue === VIDEO_CALL_STATUS.CLOSING) {
        if (hasValue(publisher)) {
          publisher.destroy();
          publisher = undefined;
        }
      }
    }
  }
);

const trackVideoWatcher = watch(
  () => props.trackStatus.video,
  (newValue, oldValue) => {
    log("UPDATE VIDEO TRACK", props.participant, props.networkQuality, newValue, oldValue);

    if (props.networkQuality === VIDEO_CALL_NETWORK_QUALITY.LOWEST) return;
    if (!hasValue(newValue) || newValue === oldValue) return;
    if (hasValue(publisher) && props.participant === PARTICIPANT.LOCAL) {
      log("Update video track", newValue);

      publisher.publishVideo(newValue);
    }
  }
);

const trackAudioWatcher = watch(
  () => props.trackStatus.audio,
  (newValue, oldValue) => {
    if (
      hasValue(publisher) &&
      hasValue(newValue) &&
      newValue !== oldValue &&
      props.participant === PARTICIPANT.LOCAL
    ) {
      log("Update audio track", newValue);

      publisher.publishAudio(newValue);
    }
  }
);

const canConnectWatch = watch(
  () => props.canConnect,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      handleConnectToVideoCall();
    }
  }
);

const audioDevicesWatcher = watch(
  () => props.devices?.audio,
  (newValue, oldValue) => {
    if (
      newValue !== oldValue &&
      newValue &&
      hasValue(publisher) &&
      isValidString(newValue?.id) &&
      publisher.getAudioSource().getSettings().deviceId !== newValue.id
    ) {
      log("Update audio device", newValue, props.participant);

      publisher.setAudioSource(newValue.id);
    }
  }
);

const videoDevicesWatcher = watch(
  () => props.devices?.video,
  (newValue, oldValue) => {
    if (
      newValue !== oldValue &&
      newValue &&
      hasValue(publisher) &&
      isValidString(newValue?.id) &&
      publisher.getVideoSource().deviceId !== newValue.id
    ) {
      log("Update video device", newValue, props.participant);

      publisher.setVideoSource(newValue.id);
    }
  }
);

const scaleWatcher = watch(
  () => props.scale,
  (newValue, oldValue) => {
    if (hasValue(newValue) && newValue !== oldValue) {
      const videos = videoWrapperRef.value!.getElementsByTagName("video");

      if (videos.length > 0) {
        videos[0].style.transform = `scale3d(${newValue}, ${newValue}, 1)`;
      }
    }
  }
);

const blurWatch = watch(
  () => props.blur,
  (newValue, oldValue) => {
    if (hasValue(newValue) && newValue !== oldValue && hasValue(publisher)) {
      if (newValue) {
        publisher.applyVideoFilter({ type: "backgroundBlur", blurStrength: "high" });
      } else {
        publisher.clearVideoFilter();
      }
    }
  }
);

/* Simulations */

const simulateLowest = () => {
  publisher!.publishVideo(false, (event) => {
    logger("Simulate lowest", event);
  });

  emit("forceStopVideo", PARTICIPANT.LOCAL);
};

const simulateHighest = () => {
  publisher!.publishVideo(true, (event) => {
    logger("Simulate highest", event);
  });

  emit("forceEnableVideo", PARTICIPANT.LOCAL);
};

const simulateLowRemote = () => {
  logger("Simulate Low remote");

  emit("changeStreamQuality", VIDEO_CALL_NETWORK_QUALITY.LOW);

  waitFor(1).then(() => {
    emit("trackStopped", TRACK_KIND.VIDEO);
  });
};

const simulateHighestRemote = () => {
  logger("Simulate highest remote");

  emit("changeStreamQuality", VIDEO_CALL_NETWORK_QUALITY.HIGHEST);

  waitFor(1).then(() => {
    syncStreamTracks();
  });
};

/* Simulations */

onMounted(async () => {
  isReadyPublisher.value = false;
  currentRetry.value = 0;
  roomId = videoCallStore.roomId;
  isErrorAvatarLocal.value = false;
  styleWrapper.value = {};

  publisher = undefined;
  subscriber = undefined;
  audioStream = undefined;

  log("Mounted", props.participant);

  if (hasValue(props.stream)) {
    initSubcriber(props.stream);
  } else {
    if (props.participant === PARTICIPANT.LOCAL) {
      log("Start local stream");

      if (!isValidString(props.token)) {
        danger("Invalid connect config");

        emit("error", { code: VIDEO_CALL_EXCEPTIONS.INVALID_PARTICIPANT_CONFIG });
      } else {
        getPublisher(
          { resolution: DEFAULT_RESOLUTION },
          { onVideoReady: handleVideoReady, onDimensionsChanged: handleUpdateStyle }
        )
          .catch(handleInitError)
          .then(handleInitPublisher);
      }
    } else if (props.participant === PARTICIPANT.SHARE_SCREEN) {
      log("Start share screen");

      getPublisher(
        { isShareScreen: true },
        { onVideoReady: handleVideoReady, onDimensionsChanged: handleUpdateStyle }
      )
        .catch(handleInitError)
        .then(handleInitPublisher);
    }
  }
});

onBeforeUnmount(() => {
  log("Unmount", props.participant);

  if (hasValue(intervalSyncTrack.value)) {
    clearInterval(intervalSyncTrack.value);
  }

  if (props.participant === PARTICIPANT.SHARE_SCREEN) {
    if (hasValue(publisher)) {
      publisher.publishVideo(false);
      publisher.publishAudio(false);

      publisher.destroy();
    }
  }
});

onUnmounted(() => {
  log("Unmounted", props.participant);

  callStatusWhatcher();
  trackVideoWatcher();
  trackAudioWatcher();
  canConnectWatch();
  audioDevicesWatcher();
  videoDevicesWatcher();
  scaleWatcher();
  blurWatch();
});
</script>

<template>
  <div
    class="participant"
    :class="{
      'participant--no_video': !trackStatus.video,
      'participant--inverse': isInvertedVideo,
    }"
    :style="styleWrapper"
    ref="videoWrapperRef"
  >
    <TcAvatar
      v-if="hasValue(props.user)"
      class="participant__avatar"
      :class="{
        'participant__avatar--no_image':
          !hasValue(props.user) || !isValidString(props.user!.avatar) || isErrorAvatarLocal,
      }"
      :size="80"
      :src="props.user!.avatar"
      :alt="props.user!.username"
      :colorIndex="hashCode(props.user!.id)"
      @errorImageLoad="() => (isErrorAvatarLocal = true)"
    />

    <template v-if="false && props.participant === PARTICIPANT.LOCAL">
      <TcButton class="button_lowest" @click="simulateLowest">
        {{
          // eslint-disable-next-line @intlify/vue-i18n/no-raw-text
          "LOWEST"
        }}
      </TcButton>

      <TcButton class="button_highest" @click="simulateHighest">
        {{
          // eslint-disable-next-line @intlify/vue-i18n/no-raw-text
          "HIGHEST"
        }}
      </TcButton>
    </template>

    <template v-if="false && props.participant === PARTICIPANT.REMOTE">
      <TcButton color="danger" class="button_low" @click="simulateLowRemote">
        {{
          // eslint-disable-next-line @intlify/vue-i18n/no-raw-text
          "LOW"
        }}
      </TcButton>

      <TcButton color="danger" class="button_highest_remote" @click="simulateHighestRemote">
        {{
          // eslint-disable-next-line @intlify/vue-i18n/no-raw-text
          "HIGHEST"
        }}
      </TcButton>
    </template>
  </div>
</template>

<style scoped lang="scss">
.button {
  &_lowest {
    position: absolute;
    z-index: 1000;
    top: 400px;
    left: -15px;
  }

  &_highest {
    position: absolute;
    z-index: 1000;
    top: 460px;
    left: -15px;
  }

  &_low {
    position: absolute;
    z-index: 1000;
    top: 520px;
    left: -15px;
  }

  &_highest_remote {
    position: absolute;
    z-index: 1000;
    top: 580px;
    left: -15px;
  }
}

.participant {
  display: flex;

  &__avatar {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 0;
    z-index: -1;
    display: flex;
    height: 100% !important;
    width: 100% !important;
    background-color: black;
  }

  &--no_video {
    .participant__avatar {
      z-index: 2;
    }
  }
}
</style>

<style lang="scss">
.participant {
  &--inverse {
    & > video {
      -webkit-transform: scaleX(-1) !important;
      transform: scaleX(-1) !important;
    }
  }

  & > video {
    @include colors.bg(content, high);

    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  &__avatar {
    & > .avatar__bg_image {
      background-size: contain;
      border-radius: 0;
    }
  }
}
</style>
